import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Thread } from '../message-center.model';
import { MatTabsModule } from '@angular/material/tabs';
import { ModalDialogComponent } from '@k12/ui-primitives';
import { ModalType } from '@k12/models';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageDateFormatter } from '@k12/messaging';
import { MatButtonModule } from '@angular/material/button';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-message-center-thread-list',
  imports: [MatProgressBar, MatTabsModule, MatListModule, MatTooltipModule, MatButtonModule],
  templateUrl: './message-center-thread-list.component.html',
  styleUrl: './message-center-thread-list.component.scss'
})
export class MessageCenterThreadListComponent implements OnChanges {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly locale: string = inject<string>(LOCALE_ID);
  private readonly promptDialog = inject(MatDialog);

  @Input() hasChanges: boolean = false;
  @Input() isLoading!: boolean;
  @Input() selectedThread: Thread | null = null;
  @Input() threads: Thread[] = [];

  @Output() threadChanged: EventEmitter<Thread> = new EventEmitter<Thread>();

  private changeConversationHelper(callback?: () => void) {
    if (!this.hasChanges) {
      // No message, allow navigation without confirmation
      if (callback) {
        callback();
      }
      return;
    }
    const promptDialogRef = this.promptDialog.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        closeButtonText: 'No',
        confirmButtonText: 'Yes',
        modalType: ModalType.ContinueCancelModal,
        title: `You have an unsent message. If you leave you will lose this message, continue?`
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });
    promptDialogRef.afterClosed().subscribe((result) => {
      if (result && callback) {
        callback();
      }
    });
  }

  public selectThread(item: Thread) {
    this.changeConversationHelper(() => {
      this.selectedThread = item;
      this.threadChanged.emit(item);
    });
  }

  public getFormattedTime(thread: Thread): string | null {
    const lastDate = thread.messages.at(-1)?.dateSent;
    return MessageDateFormatter.formatTimeAlt(lastDate, this.locale);
  }

  public getFormattedTimeTooltip(thread: Thread): string {
    const date = thread?.messages?.at(-1)?.dateSent;
    return MessageDateFormatter.formatTimeTooltip(date, this.locale);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedThread']?.currentValue) {
      // set the selected value for the option list
      this.selectedThread = changes['selectedThread'].currentValue;
      this.cdr.detectChanges();
    }
  }

  protected getMessageText(thread: Thread): string {
    const lastMessage = thread.messages?.at(-1)?.message;
    if (!lastMessage) {
      return '';
    }

    return lastMessage.length > 20
      ? lastMessage.substring(0, 20) + '...'
      : lastMessage;
  }
}
